import { useState, useEffect } from 'react'
import { useModal } from '../../contexts/ModalContext'
import { useCampaign } from '../../contexts/CampaignContext'
import { useAuth } from '../../contexts/AuthContext'
import AdminControls from '../AdminControls'
import SessionForm from '../forms/SessionForm'
import MarkdownText from '../MarkdownText'

function SessionsTab({ focusId, onConsumeFocus }) {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingSession, setEditingSession] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [selectedSession, setSelectedSession] = useState(null)
  const [filterText, setFilterText] = useState('')
  const { canEdit } = useCampaign()
  const { alert: showAlert, confirm } = useModal()
  const { authEpoch } = useAuth()

  // authEpoch: see the identical comment in ItemsTab.jsx.
  useEffect(() => {
    fetchSessions()
  }, [authEpoch])

  // Open a specific session when navigated to via a [[wiki-link]].
  useEffect(() => {
    if (!focusId || loading) return
    const target = sessions.find((s) => s.id === focusId)
    if (target) {
      setSelectedSession(target)
      onConsumeFocus?.()
    }
  }, [focusId, loading, sessions])

  useEffect(() => {
    if (!selectedSession) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedSession(null)
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [selectedSession])

  const fetchSessions = () => {
    // See ItemsTab.jsx's fetchItems for why: a non-OK response still has a
    // JSON body, so this must not hand it to setSessions() unguarded, and a
    // successful retry must clear any previous error.
    fetch('/api/sessions')
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Failed to load sessions'))))
      .then(data => {
        setSessions(Array.isArray(data) ? data : [])
        setError(null)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }

  const handleEdit = (session) => {
    setEditingSession(session)
  }

  const handleCreate = () => {
    setIsCreating(true)
  }

  const handleFormSubmit = async (formData) => {
    try {
      const isEdit = !!editingSession
      const response = await fetch('/api/sessions', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit ? { ...formData, id: editingSession.id } : formData)
      })

      if (response.ok) {
        await fetchSessions()
        showAlert({
          title: isEdit ? 'Session Updated' : 'Session Created',
          message: `Session "${formData.title}" has been ${isEdit ? 'updated' : 'created'} successfully.`,
          type: 'success'
        })
        setEditingSession(null)
        setIsCreating(false)
      } else {
        const error = await response.json()
        showAlert({
          title: 'Error',
          message: error.error || `Failed to ${isEdit ? 'update' : 'create'} session`,
          type: 'error'
        })
      }
    } catch (err) {
      showAlert({
        title: 'Error',
        message: `Failed to ${editingSession ? 'update' : 'create'} session`,
        type: 'error'
      })
    }
  }

  const handleDelete = async (session) => {
    await confirm({
      title: 'Delete Session',
      message: `Are you sure you want to delete "${session.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/sessions', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: session.id })
          })

          if (response.ok) {
            setSessions(sessions.filter(s => s.id !== session.id))
            showAlert({
              title: 'Session Deleted',
              message: `"${session.title}" has been deleted.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to delete session',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to delete session',
            type: 'error'
          })
        }
      }
    })
  }

  const handleToggleVisibility = async (session) => {
    const action = session.is_visible ? 'hide' : 'reveal'
    await confirm({
      title: `${action === 'hide' ? 'Hide' : 'Reveal'} Session`,
      message: `${action === 'hide' ? 'Hide' : 'Reveal'} "${session.title}" ${action === 'hide' ? 'from' : 'to'} players?`,
      confirmText: action === 'hide' ? 'Hide' : 'Reveal',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/sessions', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: session.id, is_visible: session.is_visible ? 0 : 1 })
          })

          if (response.ok) {
            setSessions(sessions.map(s =>
              s.id === session.id ? { ...s, is_visible: session.is_visible ? 0 : 1 } : s
            ))
            showAlert({
              title: 'Visibility Updated',
              message: `"${session.title}" is now ${action === 'hide' ? 'hidden from' : 'visible to'} players.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to update visibility',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to update visibility',
            type: 'error'
          })
        }
      }
    })
  }

  if (loading) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Sessions</h1>
          <p className="page-subtitle">Chronicle of your adventure</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>Loading sessions...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Sessions</h1>
          <p className="page-subtitle">Chronicle of your adventure</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: '#e74c3c' }}>Error loading sessions: {error}</p>
        </div>
      </div>
    )
  }

  // Filter sessions based on user role
  const trimmedFilter = filterText.trim()
  const isSearching = trimmedFilter.length > 0
  const visibleSessions = canEdit ? sessions : sessions.filter(s => s.is_visible === 1)
  const filteredSessions = visibleSessions
    .filter((session) => {
      if (isSearching && !canEdit && !session.is_visible) return false
      if (!isSearching) return true
      const searchTerm = trimmedFilter.toLowerCase()
      const title = session.title?.toLowerCase() || ''
      const summary = session.summary?.toLowerCase() || ''
      const keyEvents = session.key_events?.toLowerCase() || ''
      const sessionDate = session.session_date?.toLowerCase() || ''
      return (
        title.includes(searchTerm) ||
        summary.includes(searchTerm) ||
        keyEvents.includes(searchTerm) ||
        sessionDate.includes(searchTerm)
      )
    })
    .sort((a, b) => {
      const hiddenA = !a.is_visible && !canEdit
      const hiddenB = !b.is_visible && !canEdit
      if (hiddenA !== hiddenB) return hiddenA ? 1 : -1
      return 0
    })

  if (visibleSessions.length === 0) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Sessions</h1>
          <p className="page-subtitle">Chronicle of your adventure</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>📜</div>
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Sessions Recorded</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: canEdit ? '1.5rem' : 0 }}>
            {canEdit ? 'Add your first session to get this campaign started.' : 'Session notes will appear here after your first adventure.'}
          </p>
          {canEdit && (
            <button
              onClick={handleCreate}
              style={{
                background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
                color: '#ffffff',
                border: '1px solid #27ae60',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
              }}
            >
              + Add New Session
            </button>
          )}
        </div>

        <SessionForm
          isOpen={isCreating || !!editingSession}
          onClose={() => {
            setIsCreating(false)
            setEditingSession(null)
          }}
          session={editingSession}
          onSubmit={handleFormSubmit}
        />
      </div>
    )
  }

  return (
    <div className="tab-pane">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div className="page-header" style={{ marginBottom: 0, flexShrink: 0 }}>
          <h1>Sessions</h1>
          <p className="page-subtitle">Chronicle of your adventure</p>
        </div>
        <input
          type="text"
          placeholder="Filter sessions by title, date, summary, or key events..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{
            flex: '1 1 260px',
            minWidth: '200px',
            padding: '0.75rem 1rem',
            background: 'var(--accent-deep)',
            border: '1px solid var(--accent-teal)',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            fontSize: '0.95rem',
            fontFamily: 'inherit'
          }}
        />
        {canEdit && (
          <button
            onClick={handleCreate}
            style={{
              background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
              color: '#ffffff',
              border: '1px solid #27ae60',
              padding: '0.8rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
            }}
          >
            + Add New Session
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {filteredSessions.map(session => {
          const keyEvents = session.key_events ? JSON.parse(session.key_events) : []
          // Parse date as local date to avoid timezone offset issues
          const [year, month, day] = session.session_date.split('-')
          const sessionDate = new Date(year, month - 1, day).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

          return (
            <div
              key={session.id}
              onClick={() => setSelectedSession(session)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedSession(session)
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                background: 'var(--card-bg)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                borderLeft: '4px solid var(--accent-teal)',
                opacity: session.is_visible ? 1 : 0.6,
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 8px 18px rgba(0, 0, 0, 0.25)'
                e.currentTarget.style.borderColor = 'var(--accent-teal)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
                e.currentTarget.style.borderColor = 'var(--border-color)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                    {session.title}
                    {canEdit && !session.is_visible && <span style={{ marginLeft: '1rem', fontSize: '0.75rem', background: '#6b7f91', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px' }}>Hidden from Players</span>}
                  </h3>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{sessionDate}</div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {session.is_latest === 1 && (
                    <div style={{ background: 'var(--accent-teal)', color: 'var(--bg-primary)', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600 }}>
                      Latest
                    </div>
                  )}
                  {canEdit && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <AdminControls
                        item={session}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleVisibility={handleToggleVisibility}
                        type="session"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                <MarkdownText style={{ whiteSpace: 'pre-line' }}>{session.summary}</MarkdownText>
              </div>

              {keyEvents.length > 0 && (
                <div>
                  <h4 style={{ color: 'var(--accent-teal)', marginBottom: '0.8rem', fontSize: '1rem' }}>Key Events:</h4>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {keyEvents.map((event, index) => (
                      <span key={index} style={{ background: 'rgba(42, 85, 102, 0.3)', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                        {event}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {selectedSession && (() => {
        const keyEvents = selectedSession.key_events ? JSON.parse(selectedSession.key_events) : []
        // Parse date as local date to avoid timezone offset issues
        const [year, month, day] = selectedSession.session_date.split('-')
        const sessionDate = new Date(year, month - 1, day).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(5, 10, 18, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '1rem'
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-dark) 100%)',
                border: '2px solid var(--accent-teal)',
                borderRadius: '12px',
                maxWidth: '980px',
                width: '100%',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.6)',
                position: 'relative',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                // Padding lives on the header and scroll children rather than
                // here, so the scroll wrapper's scrollbar sits flush against
                // the panel's edge instead of 2rem inside it. overflow:hidden
                // lets the border-radius clip that scrollbar's corners.
                overflow: 'hidden'
              }}
            >
              {/* Non-scrolling header row. The Close button used to be
                  position:absolute over the content, which laid it directly on
                  top of the scroll wrapper's scrollbar; in flow it cannot. */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 1rem 0.5rem', flexShrink: 0 }}>
                <button
                  onClick={() => setSelectedSession(null)}
                  style={{
                    background: 'linear-gradient(135deg, #3a4550 0%, #2d363f 100%)',
                    color: '#ffffff',
                    border: '1px solid #5a6570',
                    borderRadius: '8px',
                    padding: '0.6rem 1.4rem',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                  aria-label="Close session details"
                >
                  Close
                </button>
              </div>

              {/* scrollbarGutter keeps the right gutter reserved whether or not
                  the content actually overflows, so short and long modals have
                  the same margins. */}
              <div style={{ overflowY: 'auto', minHeight: 0, padding: '0 1rem 2rem 2rem', scrollbarGutter: 'stable' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>{selectedSession.title}</h2>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.75rem' }}>{sessionDate}</div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {selectedSession.is_latest === 1 && (
                      <span style={{ background: 'var(--accent-teal)', color: 'var(--bg-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                        Latest
                      </span>
                    )}
                    {canEdit && !selectedSession.is_visible && (
                      <span style={{ background: '#6b7f91', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                        Hidden from Players
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                  <MarkdownText style={{ whiteSpace: 'pre-line' }}>{selectedSession.summary}</MarkdownText>
                </div>

                {keyEvents.length > 0 && (
                  <div>
                    <h4 style={{ color: 'var(--accent-teal)', marginBottom: '0.8rem', fontSize: '1rem' }}>Key Events:</h4>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {keyEvents.map((event, index) => (
                        <span key={index} style={{ background: 'rgba(42, 85, 102, 0.3)', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                          {event}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        )
      })()}

      <SessionForm
        isOpen={isCreating || !!editingSession}
        onClose={() => {
          setIsCreating(false)
          setEditingSession(null)
        }}
        session={editingSession}
        onSubmit={handleFormSubmit}
      />
    </div>
  )
}

export default SessionsTab
